import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Plus, Trash2, FileText } from 'lucide-react';

const STATUS_COLORS = { pending: 'warn', in_review: 'muted', signed: 'ok', archived: 'muted' };

const Documents = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    if (!lob) return;
    api.get(`/documents?lob_id=${lob.id}`).then((r) => setDocs(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [lob?.id]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete(`/documents/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid="documents-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Documents</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="documents-add-btn">
          <Plus size={11} style={{ marginRight: 4 }} />Add document
        </button>
      </div>

      {err && <div className="module-banner err">{err}</div>}

      <div className="content-area">
        {docs.length === 0 ? (
          <div className="empty-state" data-testid="documents-empty">
            <FileText size={36} style={{ opacity: 0.2 }} />
            <div className="ttl">No documents</div>
            <div className="sub">Add agreements, statements, applications, and signed forms here.</div>
          </div>
        ) : (
          <div className="card">
            <table className="tbl">
              <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Uploaded by</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                    <td>{d.url ? <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-active)' }}>{d.name}</a> : d.name}</td>
                    <td>{d.type || '—'}</td>
                    <td><span className={`tag-pill ${STATUS_COLORS[d.status] || 'muted'}`}>{d.status.replace('_', ' ')}</span></td>
                    <td>{d.uploaded_by || '—'}</td>
                    <td>{new Date(d.created_at).toLocaleDateString()}</td>
                    <td><button className="btn-link" onClick={() => del(d.id)} style={{ color: 'var(--error)' }} data-testid={`del-doc-${d.id}`}><Trash2 size={11} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <DocumentModal lob={lob} onClose={(saved) => { setShowCreate(false); if (saved) load(); }} />}
    </>
  );
};

const DocumentModal = ({ lob, onClose }) => {
  const [mode, setMode] = useState('upload'); // 'upload' | 'metadata'
  const [form, setForm] = useState({ lob_id: lob.id, name: '', type: '', status: 'pending', url: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(''); setProgress(0);
    try {
      if (mode === 'upload') {
        if (!file) { setErr('Pick a file to upload.'); setBusy(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('lob_id', form.lob_id);
        fd.append('name', form.name || file.name);
        fd.append('type', form.type || '');
        await api.post('/uploads/document', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
        });
      } else {
        await api.post('/documents', form);
      }
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="doc-modal">
        <div className="modal-title">Add document</div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: '0.5px solid var(--stroke-lighter)' }}>
          <button type="button" className={`settings-tab${mode === 'upload' ? ' active' : ''}`} onClick={() => setMode('upload')} data-testid="doc-mode-upload">Upload file</button>
          <button type="button" className={`settings-tab${mode === 'metadata' ? ' active' : ''}`} onClick={() => setMode('metadata')} data-testid="doc-mode-link">Link / metadata</button>
        </div>
        {mode === 'upload' && (
          <>
            <label className="f-label">File (max 25 MB)</label>
            <input className="f-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} required data-testid="doc-file" />
            <div className="modal-row" style={{ marginTop: 8 }}>
              <div><label className="f-label">Display name (optional)</label><input className="f-input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={file?.name || ''} data-testid="doc-name" /></div>
              <div><label className="f-label">Type tag</label><input className="f-input" value={form.type} onChange={(e) => set('type', e.target.value)} placeholder="e.g. Application" /></div>
            </div>
            {busy && progress > 0 && <div className="module-banner">Uploading… {progress}%</div>}
          </>
        )}
        {mode === 'metadata' && (
          <>
            <label className="f-label">Name</label>
            <input className="f-input" value={form.name} onChange={(e) => set('name', e.target.value)} required={mode === 'metadata'} data-testid="doc-name-meta" />
            <div className="modal-row">
              <div><label className="f-label">Type</label><input className="f-input" placeholder="e.g. Application" value={form.type} onChange={(e) => set('type', e.target.value)} /></div>
              <div><label className="f-label">Status</label>
                <select className="f-select" value={form.status} onChange={(e) => set('status', e.target.value)}>
                  <option value="pending">Pending</option><option value="in_review">In review</option><option value="signed">Signed</option><option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <label className="f-label">URL</label>
            <input className="f-input" placeholder="https://…" value={form.url} onChange={(e) => set('url', e.target.value)} data-testid="doc-url" />
          </>
        )}
        {err && <div className="f-error" data-testid="doc-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="doc-submit">{busy ? (mode === 'upload' ? `Uploading…` : 'Saving…') : (mode === 'upload' ? 'Upload' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

export default Documents;
